// src/cloudinaryService.js

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;
// ❌ احذف أي سطر فيه CLOUDINARY_API_SECRET

/**
 * Delivery presets for on-the-fly Cloudinary transformations.
 * Applied only at render time — stored secure_url values are never mutated.
 */
const IMAGE_PRESETS = {
  // Listing cards (grid / favourites / my posts)
  card: 'w_400,c_limit,f_auto',
  // Main ad image / large gallery view
  detail: 'w_1200,c_limit,q_auto:good,f_auto',
  // Small thumbnails under the main image
  thumbnail: 'w_150,c_limit,q_auto:good,f_auto',
  // Profile / team-up avatar
  avatar: 'w_200,h_200,c_fill,g_face,q_auto:good,f_auto',
};

/**
 * Convert a stored Cloudinary secure_url into an optimized delivery URL.
 * - Non-Cloudinary URLs and falsy values are returned unchanged (backward compatible).
 * - Works with versioned and unversioned upload paths.
 * - Does not touch Firestore or require any migration.
 *
 * @param {string} url - Original secure_url (or any image URL)
 * @param {'card'|'detail'|'thumbnail'|'avatar'|string} presetOrTransforms
 *        Named preset or a raw transformation string (e.g. 'w_300,q_auto')
 * @returns {string}
 */
export const getOptimizedImageUrl = (url, presetOrTransforms = 'card') => {
  if (!url || typeof url !== 'string') return url || '';

  // Only transform Cloudinary delivery URLs
  if (!url.includes('res.cloudinary.com') || !url.includes('/upload/')) {
    return url;
  }

  const transforms =
    IMAGE_PRESETS[presetOrTransforms] || presetOrTransforms || IMAGE_PRESETS.card;

  // Insert transformations right after /upload/ (handles /upload/v1234/... too)
  return url.replace('/upload/', `/upload/${transforms}/`);
};

/**
 * رفع صورة واحدة، مع محاولات إعادة (retry) لو فشل الطلب لسبب مؤقت
 * (انقطاع شبكة، timeout، أو رد 5xx/429 من Cloudinary)
 */
export const uploadImageToCloudinary = async (imageFile, { retries = 2, timeoutMs = 30000 } = {}) => {
  const formData = new FormData();
  formData.append('file', imageFile);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'roomate/ads');

  let lastError;

  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        { method: 'POST', body: formData, signal: controller.signal }
      );
      clearTimeout(timer);

      if (!response.ok) {
        // 429 (rate limit) أو أخطاء سيرفر (5xx) يستحق إعادة محاولة، غير كده ماينفعش
        const isRetryable = response.status === 429 || response.status >= 500;
        const err = new Error(`فشل رفع الصورة (${response.status})`);
        if (!isRetryable || attempt === retries) throw err;
        lastError = err;
      } else {
        const data = await response.json();
        return {
          secure_url: data.secure_url,
          public_id: data.public_id,
          delete_token: data.delete_token   // مهم جداً للحذف الآمن
        };
      }
    } catch (error) {
      clearTimeout(timer);
      lastError = error;
      if (attempt === retries) throw lastError;
    }

    // backoff بسيط قبل المحاولة التالية
    await new Promise((res) => setTimeout(res, 800 * (attempt + 1)));
  }

  throw lastError;
};

/**
 * رفع متعدد للصور (الدالة المستخدمة في PostAdPage و PostingTeamUp)
 *
 * بيرفع الصور على دفعات صغيرة (مش كلها مرة واحدة) عشان يقلل فرصة الفشل،
 * وبيستخدم allSettled عشان لو صورة واحدة فشلت (حتى بعد الـ retries)،
 * الصور اللي نجحت متتضيعش — بيرمي خطأ واضح فيه عدد الصور اللي فشلت بالظبط.
 */
export const uploadMultipleImages = async (imageFiles, { concurrency = 3 } = {}) => {
  if (!imageFiles || imageFiles.length === 0) return [];

  const results = [];
  const failedIndexes = [];

  for (let i = 0; i < imageFiles.length; i += concurrency) {
    const batch = imageFiles.slice(i, i + concurrency);
    const batchResults = await Promise.allSettled(
      batch.map((file) => uploadImageToCloudinary(file))
    );

    batchResults.forEach((r, idx) => {
      if (r.status === 'fulfilled') {
        results.push(r.value.secure_url);
      } else {
        failedIndexes.push(i + idx);
        console.error('خطأ في رفع صورة:', r.reason);
      }
    });
  }

  if (failedIndexes.length > 0) {
    const error = new Error(`فشل رفع ${failedIndexes.length} صورة من ${imageFiles.length}`);
    error.partialResults = results; // الروابط اللي نجحت، لو حبيت تستخدمها بدل ما تضيع كل حاجة
    error.failedCount = failedIndexes.length;
    throw error;
  }

  return results;
};

/**
 * حذف صورة باستخدام delete_token (آمن)
 */
export const deleteImageFromCloudinary = async (deleteToken) => {
  if (!deleteToken) return;

  const formData = new FormData();
  formData.append('token', deleteToken);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/destroy`,
      { method: 'POST', body: formData }
    );

    if (!response.ok) throw new Error('فشل حذف الصورة');
    return await response.json();
  } catch (error) {
    console.error('خطأ في حذف الصورة:', error);
    throw error;
  }
};

/**
 * حذف متعدد
 */
export const deleteMultipleImages = async (deleteTokens) => {
  if (!deleteTokens || deleteTokens.length === 0) return;
  try {
    const promises = deleteTokens.map(token => deleteImageFromCloudinary(token));
    await Promise.all(promises);
  } catch (error) {
    console.error('خطأ في حذف الصور:', error);
  }
};